import { createSlice, createAsyncThunk, createSelector, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';
import { getTokenRedirect } from '../../utils/authRedirect';
import { tokenRequest } from '../../authConfig';
import { Note, NoteCle } from '../../types/api';
import { LOADING_STATES, SUCCESS_MESSAGES, ERROR_MESSAGES, NOTIFICATION_SEVERITY } from '../../constants';
import { noteService, NoteModifieeAilleursError, type CapturePassage, type LostPassageMark } from '../../services/noteAPI';
import { setStatusBar } from '../modals/modalSlice';
import { executeProposal } from '../chat/chatSlice';
import { captureMobileAsync, replayCaptureQueue } from './captureQueueSlice';

// Type pour stocker les états précédents pour le rollback
interface PreviousState {
    data: Note[];
    displayedNotes: Note[];
    filteredNotes: Note[];
    sortedNotes: Note[];
}

export interface NotesState {
    data: Note[];
    activeNote: Note | null;
    status: string;
    error?: string;
    bSort: Record<string, {
        displayName: string;
        value: any;
        index: number;
    }>;
    displayedNotes: Note[];
    filteredNotes: Note[];
    sortedNotes: Note[];
    // ✨ Optimistic Updates: stockage de l'état précédent pour rollback
    _previousState: PreviousState | null;
    _pendingOptimisticIds: string[];
}

const initialState: NotesState = {
    data: [],
    activeNote: null,
    status: LOADING_STATES.IDLE,
    error: undefined,
    bSort: {
        'note-sort-button-datemod': {
            displayName: 'Date de modification',
            value: null,
            index: 1,
        },
        'note-sort-button-datecre': {
            displayName: 'Date de création',
            value: null,
            index: 2,
        },
        'note-sort-button-page': {
            displayName: 'Page',
            value: null,
            index: 3,
        },
        'note-sort-button-paragraph': {
            displayName: 'Paragraphe',
            value: null,
            index: 4,
        },
    },
    displayedNotes: [],
    filteredNotes: [],
    sortedNotes: [],
    // ✨ Optimistic Updates
    _previousState: null,
    _pendingOptimisticIds: [],
};

// Helper: trier les notes par date de modification
const sortNotesByDate = (notes: Note[]): Note[] => {
    return [...notes].sort((a: Note, b: Note) => {
        const dateA = new Date(a.updatedDate || a.createdDate || 0);
        const dateB = new Date(b.updatedDate || b.createdDate || 0);
        return dateB.getTime() - dateA.getTime();
    });
};

// Helper: mettre à jour toutes les listes triées
const updateAllLists = (state: NotesState, sortedData: Note[]) => {
    state.data = sortedData;
    state.displayedNotes = [...sortedData];
    state.filteredNotes = [...sortedData];
    state.sortedNotes = [...sortedData];
};

export const noteAsync = createAsyncThunk(
    'notes/get',
    async (instance: any) => {
        // Note: si le token a expiré, getTokenRedirect va rediriger et ne retourne jamais
        const accessToken = await getTokenRedirect(tokenRequest, instance);
        if (accessToken) {
            return await noteService.getNotes(accessToken);
        }
        throw new Error('Impossible d\'obtenir le token d\'accès');
    }
);

// Type pour les options d'historique passées à saveAsync
interface HistoryOptions {
    authorName?: string;
    collaborators?: Array<{ userId: string; displayName: string; joinedAt?: Date }>;
}

/** `updatedDate` du store (`Date` ou chaîne ISO venue du JSON) → chaîne ISO ; `undefined` si absente ou illisible. */
const isoDate = (value: Date | string | undefined | null): string | undefined => {
    if (value === undefined || value === null) return undefined;
    const date = value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
};

/** Rejet typé des thunks d'enregistrement : statut HTTP (409 = note modifiée ailleurs) et message. */
export interface UpdateNoteContentFailure { status?: number; message?: string }

export interface SaveNoteArgs {
    note: Partial<Note>;
    instance: any;
    historyOptions?: HistoryOptions;
    /**
     * Ne pas envoyer la garde de version `ifUnmodifiedSince` : en collaboration
     * (le contenu a déjà convergé par le CRDT) ou pour une reprise après un 409
     * où la personne choisit de remplacer la version en base.
     */
    sansGarde?: boolean;
}

/** Message de la barre d'état quand le serveur refuse l'écriture (409). */
export const MESSAGE_NOTE_MODIFIEE_AILLEURS = 'La note a été modifiée ailleurs depuis son ouverture. Rouvrez-la pour voir la version en base, ou enregistrez de nouveau pour la remplacer.';

export const saveAsync = createAsyncThunk<Note, SaveNoteArgs, { rejectValue: UpdateNoteContentFailure }>(
    'notes/save',
    async ({ note, instance, historyOptions, sansGarde }, { dispatch, getState, rejectWithValue }) => {
        // ✨ Optimistic Update: sauvegarder l'état actuel pour rollback potentiel
        const state = getState() as { notes: NotesState };
        // La note telle que le store la connaît : sa date porte la garde de
        // version, son `content.text` comble l'optimiste quand le client n'en
        // envoie pas (le serveur le reprojette depuis le JSON).
        const current = note._id ? state.notes.data.find(n => n._id === note._id) : undefined;
        const previousState: PreviousState = {
            data: [...state.notes.data],
            displayedNotes: [...state.notes.displayedNotes],
            filteredNotes: [...state.notes.filteredNotes],
            sortedNotes: [...state.notes.sortedNotes],
        };
        
        // Créer une note temporaire pour l'update optimistic
        const tempId = note._id || `temp_${Date.now()}`;
        const isUpdate = Boolean(note._id);
        const now = new Date();
        const optimisticNote: Note = {
            ...note,
            ...(note.content
                ? { content: { ...note.content, text: note.content.text ?? current?.content?.text } }
                : {}),
            _id: tempId,
            updatedDate: now,
            createdDate: note.createdDate ? new Date(note.createdDate) : now,
        } as Note;
        // Garde de version (C22 SP1, § 2.6) : la date de la note AVANT l'écriture
        // optimiste, qui la remplace par « maintenant ».
        const ifUnmodifiedSince = isUpdate && !sansGarde ? isoDate(current?.updatedDate) : undefined;
        
        // ✨ Appliquer l'update optimistic immédiatement
        dispatch(noteSlice.actions.optimisticSave({ note: optimisticNote, isUpdate, previousState }));
        
        try {
            const accessToken = await getTokenRedirect(tokenRequest, instance);
            if (!accessToken) {
                throw new Error('Impossible d\'obtenir le token d\'accès');
            }
            
            // Extraire les infos d'historique de la note si pas passées explicitement
            const finalHistoryOptions: HistoryOptions = historyOptions || {};
            if ((note as any).authorName && !finalHistoryOptions.authorName) {
                finalHistoryOptions.authorName = (note as any).authorName;
            }
            if ((note as any).collaborators && !finalHistoryOptions.collaborators) {
                finalHistoryOptions.collaborators = (note as any).collaborators;
            }
            
            // Nettoyer la note des propriétés d'historique avant envoi
            const { authorName: _, collaborators: __, ...cleanNote } = note as any;
            
            const result = await noteService.save(cleanNote, accessToken, finalHistoryOptions, ifUnmodifiedSince ? { ifUnmodifiedSince } : {});

            // Notification de succès
            const message = isUpdate ? SUCCESS_MESSAGES.NOTE_UPDATED : SUCCESS_MESSAGES.NOTE_CREATED;
            dispatch(setStatusBar({
                open: true,
                message,
                severity: NOTIFICATION_SEVERITY.SUCCESS
            }));

            // ✨ Confirmer l'update optimistic avec les vraies données du serveur
            dispatch(noteSlice.actions.confirmOptimisticSave({ tempId, confirmedNote: result }));

            return result;
        } catch (error: any) {
            // ✨ Rollback en cas d'erreur
            dispatch(noteSlice.actions.rollbackOptimistic(previousState));

            // Garde de version : la note a changé en base, rien n'a été écrit. Le
            // rejet est typé pour que l'appelant propose la reprise (`sansGarde`).
            if (error instanceof NoteModifieeAilleursError) {
                dispatch(setStatusBar({
                    open: true,
                    message: MESSAGE_NOTE_MODIFIEE_AILLEURS,
                    severity: NOTIFICATION_SEVERITY.ERROR
                }));
                return rejectWithValue({ status: 409, message: MESSAGE_NOTE_MODIFIEE_AILLEURS });
            }

            // Notification d'erreur
            dispatch(setStatusBar({
                open: true,
                message: `Erreur lors de la sauvegarde: ${error.message}`,
                severity: NOTIFICATION_SEVERITY.ERROR
            }));
            throw error;
        }
    }
);

export interface UpdateNoteContentArgs { noteId: string; JSON: unknown; cles: NoteCle[]; instance: any }

/**
 * Mise à jour du contenu d'une note par son JSON seul (C11 SP6, mots-clés
 * ancrés depuis le mobile) : `PUT { content: { JSON }, cles }` — jamais
 * `content.text`, que le serveur reprojette. Optimiste (le store montre les
 * marques aussitôt), retour arrière sur rejet avec `{ status, message }`.
 * La garde de version (C22 SP1, § 2.6) part de l'`updatedDate` de la note du store,
 * prise avant l'écriture optimiste : `ifUnmodifiedSince`.
 */
export const updateNoteContentAsync = createAsyncThunk<Note, UpdateNoteContentArgs, { rejectValue: UpdateNoteContentFailure }>(
    'notes/updateContent',
    async ({ noteId, JSON, cles, instance }, { dispatch, getState, rejectWithValue }) => {
        const state = getState() as { notes: NotesState };
        const current = state.notes.data.find(n => n._id === noteId);
        if (!current) return rejectWithValue({ message: 'Note introuvable' });
        const previousState: PreviousState = {
            data: [...state.notes.data],
            displayedNotes: [...state.notes.displayedNotes],
            filteredNotes: [...state.notes.filteredNotes],
            sortedNotes: [...state.notes.sortedNotes],
        };
        // Garde de version (C22 SP1, § 2.6) : la date de base est celle de la note
        // AVANT l'écriture optimiste, qui la remplace par « maintenant ». La réponse
        // du serveur (`confirmOptimisticSave`) porte l'`updatedDate` du geste suivant.
        const ifUnmodifiedSince = isoDate(current.updatedDate);
        // `content.text` reste l'ancien jusqu'à la réponse : le mobile rend le JSON, pas le texte.
        const optimisticNote: Note = { ...current, content: { ...current.content, JSON }, cles, updatedDate: new Date() };
        dispatch(noteSlice.actions.optimisticSave({ note: optimisticNote, isUpdate: true, previousState }));
        try {
            const accessToken = await getTokenRedirect(tokenRequest, instance);
            if (!accessToken) throw new Error('Impossible d\'obtenir le token d\'accès');
            const result = await noteService.updateContent(noteId, JSON, cles, accessToken, ifUnmodifiedSince ? { ifUnmodifiedSince } : undefined);
            dispatch(noteSlice.actions.confirmOptimisticSave({ tempId: noteId, confirmedNote: result }));
            return result;
        } catch (error: unknown) {
            dispatch(noteSlice.actions.rollbackOptimistic(previousState));
            if (axios.isAxiosError(error)) {
                return rejectWithValue({ status: error.response?.status, message: (error.response?.data as { message?: string } | undefined)?.message });
            }
            return rejectWithValue({ message: error instanceof Error ? error.message : 'Enregistrement impossible' });
        }
    }
);

export interface UpdateNoteTitleArgs { noteId: string; title: string; instance: any }

/**
 * Renommer une note (shell mobile, 11/10/2026) : `PUT { title }` seul.
 * Optimiste (la barre montre le nouveau titre aussitôt), retour arrière sur
 * rejet avec `{ status, message }` ; même garde de version que le contenu.
 */
export const updateNoteTitleAsync = createAsyncThunk<Note, UpdateNoteTitleArgs, { rejectValue: UpdateNoteContentFailure }>(
    'notes/updateTitle',
    async ({ noteId, title, instance }, { dispatch, getState, rejectWithValue }) => {
        const state = getState() as { notes: NotesState };
        const current = state.notes.data.find(n => n._id === noteId);
        if (!current) return rejectWithValue({ message: 'Note introuvable' });
        const previousState: PreviousState = {
            data: [...state.notes.data],
            displayedNotes: [...state.notes.displayedNotes],
            filteredNotes: [...state.notes.filteredNotes],
            sortedNotes: [...state.notes.sortedNotes],
        };
        const ifUnmodifiedSince = isoDate(current.updatedDate);
        dispatch(noteSlice.actions.optimisticSave({ note: { ...current, title, updatedDate: new Date() }, isUpdate: true, previousState }));
        try {
            const accessToken = await getTokenRedirect(tokenRequest, instance);
            if (!accessToken) throw new Error('Impossible d\'obtenir le token d\'accès');
            const result = await noteService.updateTitle(noteId, title, accessToken, ifUnmodifiedSince ? { ifUnmodifiedSince } : undefined);
            dispatch(noteSlice.actions.confirmOptimisticSave({ tempId: noteId, confirmedNote: result }));
            return result;
        } catch (error: unknown) {
            dispatch(noteSlice.actions.rollbackOptimistic(previousState));
            if (axios.isAxiosError(error)) {
                return rejectWithValue({ status: error.response?.status, message: (error.response?.data as { message?: string } | undefined)?.message });
            }
            return rejectWithValue({ message: error instanceof Error ? error.message : 'Enregistrement impossible' });
        }
    }
);

export interface UpdateNotePerimetresArgs { noteId: string; perimetres: Note['perimetres']; instance: any }

/**
 * Ranger une note dans ses dossiers (shell mobile, 19/10/2026) : `PUT { perimetres }`
 * seul. Optimiste, retour arrière sur rejet avec `{ status, message }` ; même garde
 * de version que le titre.
 */
export const updateNotePerimetresAsync = createAsyncThunk<Note, UpdateNotePerimetresArgs, { rejectValue: UpdateNoteContentFailure }>(
    'notes/updatePerimetres',
    async ({ noteId, perimetres, instance }, { dispatch, getState, rejectWithValue }) => {
        const state = getState() as { notes: NotesState };
        const current = state.notes.data.find(n => n._id === noteId);
        if (!current) return rejectWithValue({ message: 'Note introuvable' });
        const previousState: PreviousState = {
            data: [...state.notes.data],
            displayedNotes: [...state.notes.displayedNotes],
            filteredNotes: [...state.notes.filteredNotes],
            sortedNotes: [...state.notes.sortedNotes],
        };
        const ifUnmodifiedSince = isoDate(current.updatedDate);
        dispatch(noteSlice.actions.optimisticSave({ note: { ...current, perimetres, updatedDate: new Date() }, isUpdate: true, previousState }));
        try {
            const accessToken = await getTokenRedirect(tokenRequest, instance);
            if (!accessToken) throw new Error('Impossible d\'obtenir le token d\'accès');
            const result = await noteService.updatePerimetres(noteId, perimetres, accessToken, ifUnmodifiedSince ? { ifUnmodifiedSince } : undefined);
            dispatch(noteSlice.actions.confirmOptimisticSave({ tempId: noteId, confirmedNote: result }));
            return result;
        } catch (error: unknown) {
            dispatch(noteSlice.actions.rollbackOptimistic(previousState));
            if (axios.isAxiosError(error)) {
                return rejectWithValue({ status: error.response?.status, message: (error.response?.data as { message?: string } | undefined)?.message });
            }
            return rejectWithValue({ message: error instanceof Error ? error.message : 'Enregistrement impossible' });
        }
    }
);

export interface ReplacePassageArgs { noteId: string; text?: string; passage: CapturePassage; instance: any }
export interface ReplacePassageResult { note: Note; lost: LostPassageMark[] }

/**
 * Modifier le passage d'une citation ou d'un commentaire (06/10/2026) :
 * `PUT /api/notes/{noteId}/passage` avec le texte retenu et le passage de la
 * capture — le serveur reconstruit la note, le store reçoit sa réponse (pas
 * d'écriture optimiste : contenu, repère et titre viennent du serveur). Garde
 * de version depuis l'`updatedDate` de la note du store ; rejet `{ status, message }`.
 */
export const replacePassageAsync = createAsyncThunk<ReplacePassageResult, ReplacePassageArgs, { rejectValue: UpdateNoteContentFailure }>(
    'notes/replacePassage',
    async ({ noteId, text, passage, instance }, { dispatch, getState, rejectWithValue }) => {
        const state = getState() as { notes: NotesState };
        const current = state.notes.data.find(n => n._id === noteId);
        const ifUnmodifiedSince = isoDate(current?.updatedDate);
        try {
            const accessToken = await getTokenRedirect(tokenRequest, instance);
            if (!accessToken) throw new Error('Impossible d\'obtenir le token d\'accès');
            const result = await noteService.replacePassage(noteId, { ...(text !== undefined ? { text } : {}), passage, ...(ifUnmodifiedSince ? { ifUnmodifiedSince } : {}) }, accessToken);
            dispatch(noteSlice.actions.confirmOptimisticSave({ tempId: noteId, confirmedNote: result.note }));
            return result;
        } catch (error: unknown) {
            if (axios.isAxiosError(error)) {
                return rejectWithValue({ status: error.response?.status, message: (error.response?.data as { message?: string } | undefined)?.message });
            }
            return rejectWithValue({ message: error instanceof Error ? error.message : 'Enregistrement impossible' });
        }
    }
);

export const removeAsync = createAsyncThunk(
    'notes/remove',
    async ({ note, instance }: { note: Note; instance: any }, { dispatch, getState }) => {
        // ✨ Optimistic Update: sauvegarder l'état actuel pour rollback potentiel
        const state = getState() as { notes: NotesState };
        const previousState: PreviousState = {
            data: [...state.notes.data],
            displayedNotes: [...state.notes.displayedNotes],
            filteredNotes: [...state.notes.filteredNotes],
            sortedNotes: [...state.notes.sortedNotes],
        };
        
        // ✨ Supprimer optimistiquement immédiatement
        dispatch(noteSlice.actions.optimisticRemove({ noteId: note._id, previousState }));
        
        try {
            const accessToken = await getTokenRedirect(tokenRequest, instance);
            if (!accessToken) {
                throw new Error('Impossible d\'obtenir le token d\'accès');
            }
            
            const result = await noteService.delete(note, accessToken);
            
            // Notification de succès
            dispatch(setStatusBar({
                open: true,
                message: SUCCESS_MESSAGES.NOTE_DELETED,
                severity: NOTIFICATION_SEVERITY.SUCCESS
            }));
            
            return result;
        } catch (error: any) {
            // ✨ Rollback en cas d'erreur
            dispatch(noteSlice.actions.rollbackOptimistic(previousState));
            
            // Notification d'erreur
            dispatch(setStatusBar({
                open: true,
                message: `Erreur lors de la suppression: ${error.message}`,
                severity: NOTIFICATION_SEVERITY.ERROR
            }));
            throw error;
        }
    }
);

export const searchNotesAsync = createAsyncThunk(
    'notes/search',
    async ({ query, instance }: { query: string; instance: any }) => {
        const accessToken = await getTokenRedirect(tokenRequest, instance);
        if (accessToken) {
            return await noteService.search(query, accessToken);
        }
        throw new Error('Impossible d\'obtenir le token d\'accès');
    }
);

export const captureNoteAsync = createAsyncThunk(
    'notes/capture',
    async ({ text, perimetreId, passage, instance }: {
        text: string;
        perimetreId?: string;
        /** Dictée « source » (C12) : passage exact retrouvé par le serveur — la note devient une citation rattachée. */
        passage?: CapturePassage;
        instance: any;
    }, { dispatch }) => {
        const accessToken = await getTokenRedirect(tokenRequest, instance);
        if (!accessToken) {
            throw new Error('Impossible d\'obtenir le token d\'accès');
        }
        try {
            const result = passage
                ? await noteService.capture(text, perimetreId, accessToken, undefined, passage)
                : await noteService.capture(text, perimetreId, accessToken);
            dispatch(setStatusBar(passage
                ? { open: true, message: 'Citation enregistrée', severity: NOTIFICATION_SEVERITY.SUCCESS }
                : { open: true, message: 'Pensée capturée — retrouvez-la dans la boîte d\'entrée', severity: NOTIFICATION_SEVERITY.SUCCESS }));
            return result;
        } catch (error: any) {
            dispatch(setStatusBar({
                open: true,
                message: `La capture a échoué: ${error.message}`,
                severity: NOTIFICATION_SEVERITY.ERROR
            }));
            throw error;
        }
    }
);

const noteSlice = createSlice({
    name: 'notes',
    initialState,
    reducers: {
        activateNote(state, action: PayloadAction<Note | null>) {
            state.activeNote = action.payload;
        },
        setSortConfig(state, action: PayloadAction<{ key: string; config: any }>) {
            state.bSort[action.payload.key] = action.payload.config;
        },
        setBSort(state, action: PayloadAction<Record<string, any>>) {
            state.bSort = action.payload;
        },
        setDisplayedNotes(state, action: PayloadAction<Note[]>) {
            state.displayedNotes = action.payload;
        },
        setFilteredNotes(state, action: PayloadAction<Note[]>) {
            state.filteredNotes = action.payload;
        },
        setSortedNotes(state, action: PayloadAction<Note[]>) {
            state.sortedNotes = action.payload;
        },
        togglePin(state, action: PayloadAction<string>) {
            const noteId = action.payload;
            const note = state.data.find(n => n._id === noteId);
            if (note) {
                note.isPinned = !note.isPinned;
            }
        },
        // ✨ Optimistic Updates: nouvelles actions
        optimisticSave(state, action: PayloadAction<{ 
            note: Note; 
            isUpdate: boolean; 
            previousState: PreviousState 
        }>) {
            const { note, isUpdate } = action.payload;
            state._previousState = action.payload.previousState;
            state._pendingOptimisticIds.push(note._id);
            
            if (isUpdate) {
                // Mise à jour d'une note existante
                const idx = state.data.findIndex(n => n._id === note._id);
                if (idx > -1) {
                    state.data[idx] = note;
                }
            } else {
                // Nouvelle note
                state.data.unshift(note);
            }
            
            // Mettre à jour toutes les listes
            const sortedData = sortNotesByDate(state.data);
            updateAllLists(state, sortedData);
            state.activeNote = note;
        },
        confirmOptimisticSave(state, action: PayloadAction<{ 
            tempId: string; 
            confirmedNote: Note 
        }>) {
            const { tempId, confirmedNote } = action.payload;
            
            // Remplacer la note temporaire par la vraie note du serveur
            const idx = state.data.findIndex(n => n._id === tempId);
            if (idx > -1) {
                state.data[idx] = confirmedNote;
            }
            
            // Mettre à jour dans les autres listes
            const updateInList = (list: Note[]) => {
                const listIdx = list.findIndex(n => n._id === tempId);
                if (listIdx > -1) {
                    list[listIdx] = confirmedNote;
                }
            };
            updateInList(state.displayedNotes);
            updateInList(state.filteredNotes);
            updateInList(state.sortedNotes);
            
            // Mettre à jour activeNote si c'était la note modifiée
            if (state.activeNote?._id === tempId) {
                state.activeNote = confirmedNote;
            }
            
            // Retirer de la liste des IDs en attente
            state._pendingOptimisticIds = state._pendingOptimisticIds.filter(id => id !== tempId);
            state._previousState = null;
        },
        optimisticRemove(state, action: PayloadAction<{ 
            noteId: string; 
            previousState: PreviousState 
        }>) {
            const { noteId } = action.payload;
            state._previousState = action.payload.previousState;
            state._pendingOptimisticIds.push(noteId);
            
            // Supprimer de toutes les listes
            state.data = state.data.filter(n => n._id !== noteId);
            state.displayedNotes = state.displayedNotes.filter(n => n._id !== noteId);
            state.filteredNotes = state.filteredNotes.filter(n => n._id !== noteId);
            state.sortedNotes = state.sortedNotes.filter(n => n._id !== noteId);
            
            if (state.activeNote?._id === noteId) {
                state.activeNote = null;
            }
        },
        rollbackOptimistic(state, action: PayloadAction<PreviousState>) {
            // Restaurer l'état précédent
            state.data = action.payload.data;
            state.displayedNotes = action.payload.displayedNotes;
            state.filteredNotes = action.payload.filteredNotes;
            state.sortedNotes = action.payload.sortedNotes;
            state._previousState = null;
            state._pendingOptimisticIds = [];
        },
    },
    extraReducers: (builder) => {
        builder
            // Get notes
            .addCase(noteAsync.pending, (state) => {
                state.status = LOADING_STATES.LOADING;
                state.error = undefined;
            })
            .addCase(noteAsync.fulfilled, (state, action) => {
                // Trier les notes par updatedDate décroissant (plus récente en premier)
                const sortedNotes = (action.payload || []).sort((a: Note, b: Note) => {
                    const dateA = new Date(a.updatedDate || a.createdDate || 0);
                    const dateB = new Date(b.updatedDate || b.createdDate || 0);
                    return dateB.getTime() - dateA.getTime();
                });
                
                state.data = sortedNotes;
                state.status = LOADING_STATES.SUCCEEDED;
                state.displayedNotes = sortedNotes;
                state.filteredNotes = sortedNotes;
                state.sortedNotes = sortedNotes;
            })
            .addCase(noteAsync.rejected, (state, action) => {
                state.status = LOADING_STATES.FAILED;
                state.error = action.error.message || 'Erreur lors du chargement des notes';
            })
            // Save note
            .addCase(saveAsync.pending, (state) => {
                state.status = LOADING_STATES.LOADING;
            })
            .addCase(saveAsync.fulfilled, (state, action) => {
                if (!action.payload) return;
                const idx = state.data.findIndex(n => n._id === action.payload._id);
                
                if (idx > -1) {
                    // Mise à jour d'une note existante - remplacer dans data
                    state.data[idx] = action.payload;
                } else {
                    // Nouvelle note - ajouter à data
                    state.data.push(action.payload);
                }
                
                // Re-trier toutes les listes par updatedDate décroissant
                const sortedData = [...state.data].sort((a: Note, b: Note) => {
                    const dateA = new Date(a.updatedDate || a.createdDate || 0);
                    const dateB = new Date(b.updatedDate || b.createdDate || 0);
                    return dateB.getTime() - dateA.getTime();
                });
                
                state.data = sortedData;
                state.displayedNotes = [...sortedData];
                state.filteredNotes = [...sortedData];
                state.sortedNotes = [...sortedData];
                state.activeNote = action.payload;
                state.status = LOADING_STATES.SUCCEEDED;
            })
            .addCase(saveAsync.rejected, (state, action) => {
                state.status = LOADING_STATES.FAILED;
                state.error = action.error.message || 'Erreur lors de la sauvegarde';
            })
            // Remove note
            .addCase(removeAsync.pending, (state) => {
                state.status = LOADING_STATES.LOADING;
            })
            .addCase(removeAsync.fulfilled, (state, action) => {
                if (!action.payload) return;
                state.data = state.data.filter(n => n._id !== action.payload._id);
                state.activeNote = null;
                state.status = LOADING_STATES.SUCCEEDED;
                // Mettre à jour les autres listes
                state.displayedNotes = state.displayedNotes.filter(n => n._id !== action.payload._id);
                state.filteredNotes = state.filteredNotes.filter(n => n._id !== action.payload._id);
                state.sortedNotes = state.sortedNotes.filter(n => n._id !== action.payload._id);
            })
            .addCase(removeAsync.rejected, (state, action) => {
                state.status = LOADING_STATES.FAILED;
                state.error = action.error.message || 'Erreur lors de la suppression';
            })
            // Search notes
            .addCase(searchNotesAsync.fulfilled, (state, action) => {
                state.filteredNotes = action.payload || [];
                state.displayedNotes = action.payload || [];
            })
            .addCase(searchNotesAsync.rejected, (state, action) => {
                state.error = action.error.message || 'Erreur lors de la recherche';
            })
            // Capture rapide (C4)
            .addCase(captureNoteAsync.fulfilled, (state, action) => {
                if (!action.payload) return;
                state.data.push(action.payload);
                updateAllLists(state, sortNotesByDate(state.data));
            })

            // Capture mobile (C11 SP2) : la note n'entre dans le store qu'au succès de l'envoi.
            .addCase(captureMobileAsync.fulfilled, (state, action) => {
                if (action.payload.queued) return;
                state.data.push(action.payload.note);
                updateAllLists(state, sortNotesByDate(state.data));
            })
            .addCase(replayCaptureQueue.fulfilled, (state, action) => {
                if (action.payload.sent.length === 0) return;
                state.data.push(...action.payload.sent);
                updateAllLists(state, sortNotesByDate(state.data));
            })

            // ── Cross-slice : note créée/modifiée/supprimée via une proposition RAG Écriture ──
            // Sans ça, valider une action dans l'Assistant écrivait en base mais laissait
            // les listes du store intactes : il fallait rafraîchir la page pour voir la note.
            .addCase(executeProposal.fulfilled, (state, action) => {
                const result = action.payload;
                if (!result.success || result.targetType !== 'note') return;

                const resultData = result.resultData as Note | undefined;
                const targetId = resultData?._id || result.resultId;
                if (!targetId) return;

                if (result.action === 'delete') {
                    state.data = state.data.filter(n => n._id !== targetId);
                    state.displayedNotes = state.displayedNotes.filter(n => n._id !== targetId);
                    state.filteredNotes = state.filteredNotes.filter(n => n._id !== targetId);
                    state.sortedNotes = state.sortedNotes.filter(n => n._id !== targetId);
                    if (state.activeNote?._id === targetId) {
                        state.activeNote = null;
                    }
                    return;
                }

                if (!resultData) return;

                const idx = state.data.findIndex(n => n._id === targetId);
                if (idx > -1) {
                    state.data[idx] = resultData;
                } else if (state.status === LOADING_STATES.IDLE) {
                    // Liste jamais chargée : ne rien injecter, le chargement initial
                    // (LoadInitialDataWithPerimetres) rapportera la note de toute façon.
                    return;
                } else {
                    state.data.push(resultData);
                }

                // Les quatre listes doivent rester alignées et triées (cf. saveAsync)
                updateAllLists(state, sortNotesByDate(state.data));

                if (state.activeNote?._id === targetId) {
                    state.activeNote = resultData;
                }
            });
    }
});

// Sélecteurs typés
// Repli partagé : `|| []` fabriquait un tableau neuf à chaque appel, donc un
// nouveau résultat à identité constante — react-redux le signale et re-rend.
const NO_NOTES: Note[] = [];
export const getNotes = (state: any) => state.notes?.data || NO_NOTES;
export const getActiveNote = (state: any) => state.notes?.activeNote || null;
export const getNotesStatus = (state: any) => state.notes?.status || LOADING_STATES.IDLE;
export const getNotesError = (state: any) => state.notes?.error;
export const getDisplayedNotes = (state: any) => state.notes?.displayedNotes || [];
export const getFilteredNotes = (state: any) => state.notes?.filteredNotes || [];
export const getSortedNotes = (state: any) => state.notes?.sortedNotes || [];
export const getSortConfig = (state: any) => state.notes?.bSort || {};
export const getBSort = (state: any) => state.notes?.bSort || {};

// Boîte d'entrée (C4) — mémoïsés : un sélecteur qui filtre/trie retourne un
// nouveau tableau à chaque appel sans createSelector, donc re-render en rafale.
export const getUntriagedNotes = createSelector(
    [(state: any) => state.notes?.data ?? []],
    (data: Note[]) => data
        .filter(n => !!n.untriagedAt)
        .sort((a, b) => new Date(a.untriagedAt as string).getTime() - new Date(b.untriagedAt as string).getTime())
);
export const getUntriagedCount = createSelector([getUntriagedNotes], notes => notes.length);

// Compatibility selectors for testreact patterns
export const selectAllNotes = (state: any) => state.notes?.data || [];
export const fetchNotes = noteAsync;
export const addNote = saveAsync;
export const deleteNote = removeAsync;
export const updateNote = saveAsync;

export const { 
    activateNote, 
    setSortConfig, 
    setBSort,
    setDisplayedNotes, 
    setFilteredNotes, 
    setSortedNotes,
    togglePin,
    // ✨ Optimistic Updates actions
    optimisticSave,
    confirmOptimisticSave,
    optimisticRemove,
    rollbackOptimistic,
} = noteSlice.actions;

export default noteSlice.reducer;
